import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { APIClient } from '../../../utils/APIClient';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { API_URL } from "../../../settings/local_settings";
import { AgentWebSocketsClient } from '../../../websockets/WebSocketsClient';
import { AgentContextSelector } from './AgentContextSelector';
import { AgentEmptyState } from './AgentEmptyState';
import { CollaborativeMessageBubble } from '../../agent/shared/CollaborativeMessageBubble';
import { AgentThinkingIndicator } from './AgentThinkingIndicator';
import { AgentInputArea } from './AgentInputArea';
import { ChatHeader } from '../../agent/shared/ChatHeader';
import { useAgentSessions } from '../../agent/shared/AgentSessionContext';
import { ConfigureEventsModal } from '../../data_explorer/ConfigureEventsModal';


const STEP_LABELS = {
    processing: 'agent_chat.step_processing',
    discovery: 'agent_chat.step_discovery',
    validation: 'agent_chat.step_validation',
    execution: 'agent_chat.step_execution',
    save: 'agent_chat.step_save',
};

export const ClientAgentChat = ({ clientId, sessionId, onSessionCreated, onSessionNameUpdate, adAccounts, businessUnits }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { activeSession, updateSessionLocal, entryPoint } = useAgentSessions();
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [currentStep, setCurrentStep] = useState(null);
    const [scope, setScope] = useState(null);
    const [debugMode, setDebugMode] = useState(false);
    const [showEventsConfigModal, setShowEventsConfigModal] = useState(false);
    const [unconfiguredAccounts, setUnconfiguredAccounts] = useState([]);
    const [pendingScope, setPendingScope] = useState(null);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (sessionId) {
            APIClient.getAgentSessionHistory(sessionId).then(data => {
                if (data && data.messages) {
                    setMessages(data.messages);
                }
            });
            setScope({ adAccountId: null, businessUnitId: null });
        } else {
            setMessages([]);
            setScope(null);
        }
    }, [sessionId]);

    useEffect(() => {
        const hasData = (adAccounts && adAccounts.length > 0) || (businessUnits && businessUnits.length > 0);
        if (!sessionId && !hasData && scope === null && adAccounts !== undefined && businessUnits !== undefined) {
            setScope({ adAccountId: null, businessUnitId: null });
        }
    }, [sessionId, adAccounts, businessUnits, scope]);

    const apiClient = useMemo(() => new APIClient(`${API_URL}agent/chat/`), []);

    const scrollToBottom = useCallback(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isSending, scrollToBottom]);

    const handleScopeSelected = useCallback((selectedScope) => {
        if (selectedScope.mode === 'accounts') {
            new APIClient(`${API_URL}client/${clientId}/ad-accounts/events-check`)
                .call('GET')
                .then(response => {
                    if (response.data && response.data.needs_configuration) {
                        setUnconfiguredAccounts(response.data.unconfigured_accounts);
                        setShowEventsConfigModal(true);
                        setPendingScope(selectedScope);
                    } else {
                        setScope(selectedScope);
                    }
                })
                .catch(() => setScope(selectedScope));
        } else {
            setScope(selectedScope);
        }
    }, [clientId]);

    const handlePromptClick = useCallback((promptText) => {
        setInputValue(promptText);
        setTimeout(() => {
            const syntheticSend = document.querySelector('.agent-input__send-btn');
            if (syntheticSend && !syntheticSend.disabled) {
                syntheticSend.click();
            }
        }, 50);
    }, []);

    const handleSend = useCallback(async () => {
        const trimmed = (inputValue || '').trim();
        if (!trimmed || isSending) return;
        setIsSending(true);
        setCurrentStep(null);

        const newUserMsg = { role: 'user', text: trimmed };
        setMessages(prev => [...prev, newUserMsg]);
        setInputValue('');

        const requestId = crypto.randomUUID();
        let channel = null;

        channel = AgentWebSocketsClient.subscribe(`agent-progress-${requestId}`);
        if (channel) {
            channel.bind('agent-step', (data) => {
                const label = STEP_LABELS[data.step];
                setCurrentStep(label ? t(label) : data.step);
            });
        }

        try {
            const payload = {
                message: trimmed,
                request_id: requestId,
                agency_client_id: parseInt(clientId),
                debug: debugMode
            };
            if (sessionId) payload.session_id = sessionId;
            if (scope) {
                if (scope.adAccountId) payload.ad_account_id = scope.adAccountId;
                if (scope.businessUnitId) payload.business_unit_id = scope.businessUnitId;
            }

            const response = await apiClient.call('POST', payload);

            if (!response) {
                setMessages(prev => [...prev, { role: 'error', text: t('agent_chat.error_no_response') }]);
                setIsSending(false);
                return;
            }

            if (response.status === 'ERROR') {
                setMessages(prev => [...prev, { role: 'error', text: response.error_message || t('agent_chat.error_generic') }]);
                setIsSending(false);
                return;
            }

            const reply = response.data.reply || '';
            const newSessionId = response.data.session_id;
            const sessionName = response.data.session_name;
            const toolTrace = response.data.tool_trace;

            if (!sessionId && newSessionId) {
                const basePath = entryPoint === 'portal' ? '/portal/agent' : `/client/${clientId}/agent`;
                navigate(`${basePath}/${newSessionId}`);
                if (onSessionCreated) onSessionCreated(newSessionId);
            }

            if (sessionName && onSessionNameUpdate) {
                onSessionNameUpdate(newSessionId || sessionId, sessionName);
            }

            if (reply) {
                const assistantMsg = { role: 'assistant', text: reply };
                if (toolTrace) assistantMsg.tool_trace = toolTrace;
                setMessages(prev => [...prev, assistantMsg]);
            }
        } catch (e) {
            setMessages(prev => [...prev, { role: 'error', text: t('agent_chat.error_send_failed') }]);
        } finally {
            if (channel) {
                channel.unbind_all();
                AgentWebSocketsClient.unsubscribe(`agent-progress-${requestId}`);
            }
            setCurrentStep(null);
            setIsSending(false);
        }
    }, [apiClient, inputValue, isSending, sessionId, clientId, scope, debugMode, navigate, t, onSessionCreated, onSessionNameUpdate, entryPoint]);

    // Resuelve scope label y tipo para el header
    const scopeInfo = useMemo(() => {
        if (!scope) return null;
        const hasData = (adAccounts && adAccounts.length > 0) || (businessUnits && businessUnits.length > 0);
        if (!hasData) return null;
        if (scope.adAccountId && adAccounts) {
            const acc = adAccounts.find(a => a.id === scope.adAccountId);
            if (acc) return { label: acc.name, type: 'accounts' };
        }
        if (scope.businessUnitId && businessUnits) {
            const bu = businessUnits.find(b => b.id === scope.businessUnitId);
            if (bu) return { label: bu.name, type: 'bus' };
        }
        if (scope.mode === 'accounts') return { label: t('agent_chat.context_all_accounts'), type: 'accounts' };
        if (scope.mode === 'bus') return { label: t('agent_chat.context_all_bus'), type: 'bus' };
        return { label: t('agent_chat.context_all_accounts'), type: 'accounts' };
    }, [scope, adAccounts, businessUnits, t]);

    const isSharedSession = activeSession && activeSession.visibility && activeSession.visibility !== 'PRIVATE';

    const hasContextData = (adAccounts && adAccounts.length > 0) || (businessUnits && businessUnits.length > 0);
    const showContextSelector = !sessionId && scope === null && hasContextData;
    const showEmptyState = !sessionId && (scope !== null || !hasContextData) && messages.length === 0 && !isSending;

    return (
        <div className="agent-chat">
            <ChatHeader
                scopeLabel={scopeInfo?.label}
                scopeType={scopeInfo?.type}
                session={activeSession}
                debugToggle={
                    <button
                        className={`agent-chat__debug-btn ${debugMode ? '-active' : ''}`}
                        onClick={() => setDebugMode(prev => !prev)}
                        title="Debug mode"
                    >
                        <i className="fas fa-bug"></i>
                    </button>
                }
            />

            <div className="agent-chat__messages">
                {showContextSelector && (
                    <AgentContextSelector
                        adAccounts={adAccounts}
                        businessUnits={businessUnits}
                        onScopeSelected={handleScopeSelected}
                    />
                )}

                {showEmptyState && (
                    <AgentEmptyState onPromptClick={handlePromptClick} />
                )}

                {!showContextSelector && !showEmptyState && messages.map((m, idx) => (
                    <CollaborativeMessageBubble key={idx} message={m} isSharedSession={isSharedSession} />
                ))}

                {isSending && (
                    <AgentThinkingIndicator stepLabel={currentStep} />
                )}
                <div ref={messagesEndRef} />
            </div>

            {!showContextSelector && (
                <AgentInputArea
                    value={inputValue}
                    onChange={setInputValue}
                    onSend={handleSend}
                    disabled={isSending}
                />
            )}

            <ConfigureEventsModal
                isOpen={showEventsConfigModal}
                unconfiguredAccounts={unconfiguredAccounts}
                onComplete={() => {
                    setShowEventsConfigModal(false);
                    if (pendingScope) {
                        setScope(pendingScope);
                        setPendingScope(null);
                    }
                }}
                toggle={() => {
                    setShowEventsConfigModal(false);
                    setPendingScope(null);
                }}
            />
        </div>
    );
};
